import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "../../../redux/actions/modals";
import ModalTemplate from "./template";
import Button from "../button";
import ImageCropper from "../../editInfo/imageCropper/ImageCropper";
import logo from "../../../static/images/logo/logo.png";
import { State } from "../../../redux/reducers/root";
import { UploadImage } from "../../../redux/actions/edit-info";

const SelectImageType = ({ name, icon }: { name: string; icon: string }) => {
  return (
    <div className="flex flex-col items-center gap-2 w-fit">
      <div
        className={`flex items-center justify-center border w-[62px] h-[62px]  border-solid rounded-full ${
          icon === "far fa-camera fa-2x" ? "border-medium-gray" : "border-pkc-secondary"
        }`}
      >
        <i className={`${icon === "far fa-camera fa-2x" ? "text-pkc-medium-gray" : "text-pkc-primary"} ${icon}`} />
      </div>
      <div
        className={`text-sm font-medium ${
          icon === "far fa-camera fa-2x" ? "text-pkc-medium-gray" : "text-pks-form-text"
        }`}
      >
        {name}
      </div>
    </div>
  );
};

const EditProfileImage: React.FC = () => {
  const dispatch = useDispatch();
  const [profilePicture, setProfilePicture] = useState<any>();
  const [finalImageUrl, setFinalImageUrl] = useState<string>("");
  const [finalImageName, setFinalImageName] = useState<string>("");
  const doctor = useSelector((state: State) => state.selectDoctor);
  const [uploadError, setUploadError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [checkFileFormat, setCheckFileFormat] = useState<boolean>(false);
  const uploadedImages = useSelector((state: State) => state.profileImage);

  const addLogoToProfileImage = (image: any, zoom: number) => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    const img = new Image();
    img.crossOrigin = "Anonymous";

    img.onload = () => {
      const canvasWidth = img.width * (zoom === 0 ? 1 : zoom);
      const canvasHeight = img.height * (zoom === 0 ? 1 : zoom);

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      if (context) {
        context.drawImage(img, 0, 0, canvasWidth, canvasHeight);

        // adding logo image as a watermark
        const logoImg = new Image();
        logoImg.src = logo;

        logoImg.onload = () => {
          const logoWidth = uploadedImages.type === "officeImage" ? (canvasWidth / 220) * 60 : (canvasWidth / 220) * 80;
          const logoHeight = (canvasHeight / 220) * 80;

          const logoX = (canvasWidth - logoWidth) / 1;
          // const logoY = canvasHeight - logoHeight - 40;
          const logoY = canvasHeight - logoHeight - (canvasHeight / 220) * 10;

          // Draw the logo at the calculated position
          context.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);

          setFinalImageUrl(canvas.toDataURL());
        };
      }
    };
    img.src = image; // Assuming finalImage is the blob URL
  };

  useEffect(() => {
    addLogoToProfileImage(profilePicture, 0);
  }, [profilePicture]);

  const dataURLtoBlob = (dataURL: string): Blob | null => {
    // Split the data URL to get the MIME type and the base64 data
    const parts = dataURL.split(";base64,");
    if (parts.length !== 2) {
      return null;
    }

    // Extract the MIME type and base64 data
    const contentType = parts[0].split(":")[1];
    const rawBase64 = parts[1];

    // Convert the base64 data to a Uint8Array
    const byteCharacters = atob(rawBase64);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i);
    }

    // Create a Blob object from the Uint8Array
    return new Blob([byteArray], { type: contentType });
  };

  const handleUploadImg = async () => {
    const blob = dataURLtoBlob(finalImageUrl);
    if (blob !== null) {
      try {
        const uploadedImg = (await dispatch(
          UploadImage(
            doctor.id!,
            finalImageName,
            blob,
            uploadedImages.type === "officeImage" ? "تغییر عکس گالری" : "تغییر عکس پروفایل"
          )
        )) as any;

        uploadedImages.type === "officeImage"
          ? dispatch({
              type: "SET_OFFICE_IMAGE",
              payload: {
                image: { file: dataURLtoBlob(finalImageUrl), id: uploadedImg.payload.fileId },
              },
            })
          : dispatch({
              type: "SET_PROFILE_IMAGE",
              payload: {
                image: dataURLtoBlob(finalImageUrl),
                id: uploadedImg.payload.fileId,
              },
            });
        dispatch(hideModal());
        setProfilePicture(false);
      } catch {
        setUploadError(`خطا در آپلود عکس پروفایل`);
      } finally {
        setLoading(false);
      }
    }
  };

  const saveProfileImage = () => {
    setLoading(true);
    setUploadError("");
    handleUploadImg();
  };

  useEffect(() => {
    setCheckFileFormat(false);
  }, [uploadedImages.type]);

  return (
    <>
      {!profilePicture ? (
        <ModalTemplate
          title={`آپلود تصویر ${uploadedImages.type === "officeImage" ? "گالری" : "پروفایل"}`}
          icon="circle-user"
        >
          <div className="flex flex-col">
            <div
              className="flex gap-12 p-5 pb-5 leading-8 text-normal item-center"
              onClick={() => setCheckFileFormat(false)}
            >
              <SelectImageType name="دوربین" icon="far fa-camera fa-2x" />

              <input
                type="file"
                id="uploadProfileImage"
                hidden
                accept=".jpeg, .jpg"
                onChange={(e) => {
                  if (e.target.files) {
                    if (e.target.files[0].type !== "image/jpg" && e.target.files[0].type !== "image/jpeg") {
                      setCheckFileFormat(true);
                    } else {
                      setCheckFileFormat(false);
                      setFinalImageName(e.target.files[0].name);
                      setProfilePicture(URL.createObjectURL(e.target.files[0]));
                    }
                  }
                }}
              />

              <label htmlFor={"uploadProfileImage"} className="cursor-pointer">
                <SelectImageType name="گالری" icon="far fa-image fa-2x" />
              </label>
            </div>
            {checkFileFormat && (
              <div className="text-xs text-pkc-red">
                فرمت فایل انتخاب شده مجاز نمی باشد لطفا فایل با فرمت jpg یا jpeg انتخاب کنید
              </div>
            )}
          </div>
        </ModalTemplate>
      ) : (
        <ModalTemplate>
          <div className="text-sm font-bold text-pkc-form-icon">
            تنظیم تصویر {uploadedImages.type === "officeImage" ? "گالری" : "پروفایل"}
          </div>
          <ImageCropper image={profilePicture} setFinalImage={addLogoToProfileImage} />

          <div className="flex items-center justify-between w-full mt-9">
            <i className="text-pkc-description-text fa fa-image fa-lg" />
            <i className="text-pkc-description-text far fa-image fa-lg" />
          </div>
          <div className="mt-3 text-sm font-bold text-pkc-form-icon">پیش نمایش نهایی تصویر شما</div>
          <div className="flex justify-center mt-3">
            <img
              src={finalImageUrl}
              alt="Image"
              sizes="100vw"
              className={`rounded-[2px] ${
                uploadedImages.type === "officeImage" ? "w-[114px] h-[85px]" : "w-[85px] h-[114px]"
              }`}
            />
          </div>
          <div className="flex items-center justify-between gap-2 mt-3 mb-2">
            <button
              className="w-full bg-white border-1 text-pkc-form-icon border-pkc-form-icon p-2.5 rounded focus:outline-none"
              onClick={() => setProfilePicture(null)}
            >
              انصراف
            </button>
            <Button className="w-full" onClick={saveProfileImage} disabled={loading}>
              {!loading ? " تایید و ذخیره" : <i className={`text-gray-500 fas fa-spinner animate-spin`} />}
            </Button>
          </div>
          {uploadError && <div className="text-pkc-red">{uploadError}</div>}
        </ModalTemplate>
      )}
    </>
  );
};

export default EditProfileImage;
